import React from 'react';
import { Mail, Phone, Calendar, Text, ArrowRight, Package, DollarSign, Truck } from 'lucide-react';
import { colors, typography, spacing, components, transitions, shadows } from '../styles/shared';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[6],
  },
  title: {
    ...typography.h3,
    color: colors.gray[900],
    margin: 0,
  },
  description: {
    ...typography.body1,
    color: colors.gray[600],
    margin: 0,
    maxWidth: '600px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: spacing[4],
  },
  card: {
    ...components.card,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[4],
    cursor: 'pointer',
    transition: transitions.smooth,
    padding: spacing[6],
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.lg,
      borderColor: colors.primary[400],
    },
  },
  selectedCard: {
    borderColor: colors.primary[600],
    backgroundColor: colors.primary[50],
    boxShadow: `0 0 0 1px ${colors.primary[600]}`,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: colors.primary[100],
    color: colors.primary[600],
    transition: transitions.base,
  },
  cardTitle: {
    ...typography.h4,
    color: colors.gray[900],
    margin: 0,
  },
  cardDescription: {
    ...typography.body2,
    color: colors.gray[600],
    margin: 0,
  },
  arrow: {
    marginLeft: 'auto',
    opacity: 0,
    transform: 'translateX(-8px)',
    transition: transitions.smooth,
    color: colors.primary[600],
  },
  arrowVisible: {
    opacity: 1,
    transform: 'translateX(0)',
  },
};

const QR_TYPES = [
  {
    id: 'email',
    title: 'Email',
    description: 'Generate QR codes that open email clients with pre-filled addresses',
    icon: Mail,
  },
  {
    id: 'contact',
    title: 'Contact',
    description: 'Create QR codes for contact information that can be saved to phones',
    icon: Phone,
  },
  {
    id: 'calendar',
    title: 'Calendar Event',
    description: 'Generate QR codes for calendar events with dates and details',
    icon: Calendar,
  },
  {
    id: 'plain_text',
    title: 'Plain Text',
    description: 'Create QR codes with custom text content from your data',
    icon: Text,
  },
  {
    id: 'product',
    title: 'Product',
    description: 'Generate QR codes containing product information and details',
    icon: Package,
  },
  {
    id: 'pricing_inventory',
    title: 'Pricing & Inventory',
    description: 'Create QR codes with pricing, stock levels, and batch tracking information',
    icon: DollarSign,
  },
  {
    id: 'tracking_logistics',
    title: 'Tracking & Logistics',
    description: 'Generate QR codes for supply chain tracking and logistics information',
    icon: Truck,
  },
];

const SelectType = ({ selectedType, onSelect }) => {
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Select QR Code Type</h2>
      <p style={styles.description}>
        Choose the type of QR code you want to generate. Each type supports different data fields from your CSV file.
      </p>
      <div style={styles.grid}>
        {QR_TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = selectedType === type.id;
          
          return (
            <div
              key={type.id}
              style={{
                ...styles.card,
                ...(isSelected && styles.selectedCard),
              }}
              onClick={() => onSelect(type.id)}
            >
              <div style={styles.iconWrapper}>
                <Icon size={24} />
              </div>
              <div>
                <h3 style={styles.cardTitle}>{type.title}</h3>
                <p style={styles.cardDescription}>{type.description}</p>
              </div>
              <ArrowRight
                size={20}
                style={{
                  ...styles.arrow,
                  ...(isSelected && styles.arrowVisible),
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SelectType;
